import types from '../../../mutation-types';
import MessageApi from 'dashboard/api/inbox/message';
import {
  withConversationHistoryRequest,
  captureConversationHistoryWindow,
} from '../helpers/historyRequests';
import {
  getConversationPositionRestore,
  getConversationRouteId,
  clearConversationPosition,
} from '../helpers/historyPosition';
import { restoreHistory } from '../helpers/restoreHistory';

const applyMetadata = (context, conversationId, meta) => {
  context.commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
    id: conversationId,
    data: meta,
  });
};

export default {
  cancelConversationHistoryRestore(context, conversationId) {
    clearConversationPosition(context.rootState);
    captureConversationHistoryWindow(context, conversationId, true);
  },
  fetchPreviousMessages: (context, data) =>
    withConversationHistoryRequest(
      context,
      { type: 'previous', ...data },
      async () => {
        const isCurrent = captureConversationHistoryWindow(
          context,
          data.conversationId
        );
        const {
          data: { meta, payload },
        } = await MessageApi.getPreviousMessages(data);
        if (!isCurrent()) return false;
        applyMetadata(context, data.conversationId, meta);
        context.commit(types.SET_PREVIOUS_CONVERSATIONS, {
          id: data.conversationId,
          data: payload,
        });
        context.dispatch('autoTranslateMessages', data.conversationId);
        if (!payload.length)
          context.commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
        const chat = context.state.allConversations.find(
          item => item.id === data.conversationId
        );
        if (chat.historyError === 'previous') {
          context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
            id: data.conversationId,
            error: false,
          });
        }
        return true;
      }
    ),

  async setActiveChat(context, { data, after, retry = false }) {
    const position = getConversationPositionRestore(context, data.id);
    const routeTarget =
      Number(getConversationRouteId(context)) === data.id
        ? context.rootState.route?.query?.messageId
        : undefined;
    const target = after ?? routeTarget;
    const cursor = position || !target ? undefined : Number(target);
    if (cursor) clearConversationPosition(context.rootState);
    context.commit(types.SET_CURRENT_CHAT_WINDOW, data);
    if (data.dataFetched === true)
      return captureConversationHistoryWindow(context, data.id);
    if (data.dataFetched === false && !retry) return null;
    return withConversationHistoryRequest(
      context,
      {
        type: 'hydrate',
        conversationId: data.id,
        after: cursor,
        anchorId: position?.anchorId,
      },
      async () => {
        const isCurrent = captureConversationHistoryWindow(
          context,
          data.id,
          true
        );
        context.commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
        context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
          id: data.id,
          error: false,
        });
        try {
          const anchorId =
            position && !position.atEnd ? position.anchorId : cursor;
          const loaded = anchorId
            ? await restoreHistory(context, data, anchorId, isCurrent)
            : await context.dispatch('fetchPreviousMessages', {
                after: cursor,
                before: data.messages[0]?.id,
                conversationId: data.id,
              });
          if (loaded && isCurrent()) {
            context.commit(types.SET_CHAT_DATA_FETCHED, data.id);
            return isCurrent;
          }
        } catch (error) {
          if (isCurrent())
            context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
              id: data.id,
              error: 'initial',
              after: cursor,
            });
        }
        return null;
      }
    );
  },

  fetchMessageHistory: (context, { conversationId, messageId }) =>
    withConversationHistoryRequest(
      context,
      { type: 'target', conversationId, anchorId: Number(messageId) },
      async () => {
        clearConversationPosition(context.rootState);
        const isCurrent = captureConversationHistoryWindow(
          context,
          conversationId,
          true
        );
        const chat = context.state.allConversations.find(
          item => item.id === conversationId
        );
        context.commit(types.CLEAR_CHAT_DATA_FETCHED, conversationId);
        context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
          id: conversationId,
          error: false,
        });
        try {
          const loaded = await restoreHistory(
            context,
            chat,
            messageId,
            isCurrent
          );
          if (loaded && isCurrent()) {
            context.commit(types.SET_CHAT_DATA_FETCHED, conversationId);
            return isCurrent;
          }
        } catch (error) {
          if (isCurrent())
            context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
              id: conversationId,
              error: 'target',
              after: Number(messageId),
            });
        }
        return null;
      }
    ),

  fetchNewerMessages: (context, conversationId) => {
    const chat = context.state.allConversations.find(
      item => item.id === conversationId
    );
    const after = chat.historyCursor;
    return withConversationHistoryRequest(
      context,
      { type: 'newer', conversationId, after },
      async () => {
        const isCurrent = captureConversationHistoryWindow(
          context,
          conversationId
        );
        try {
          const {
            data: { meta, payload },
          } = await MessageApi.getPreviousMessages({ conversationId, after });
          if (!isCurrent()) return false;
          applyMetadata(context, conversationId, meta);
          context.commit(types.APPEND_CONVERSATION_HISTORY, {
            id: conversationId,
            data: payload,
            hasMoreAfter: payload.length === 100,
          });
          context.dispatch('autoTranslateMessages', conversationId);
          return { empty: payload.length === 0 };
        } catch (error) {
          if (isCurrent())
            context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
              id: conversationId,
              error: 'newer',
            });
          return false;
        }
      }
    );
  },

  fetchLatestMessages: (context, conversationId) =>
    withConversationHistoryRequest(
      context,
      { type: 'latest', conversationId },
      async () => {
        clearConversationPosition(context.rootState);
        const isCurrent = captureConversationHistoryWindow(
          context,
          conversationId,
          true
        );
        context.commit(types.CLEAR_CHAT_DATA_FETCHED, conversationId);
        try {
          const {
            data: { meta, payload },
          } = await MessageApi.getPreviousMessages({ conversationId });
          if (!isCurrent()) return false;
          applyMetadata(context, conversationId, meta);
          context.commit(types.SET_CONVERSATION_HISTORY_WINDOW, {
            id: conversationId,
            data: payload,
            hasMoreAfter: false,
            allMessagesLoaded: payload.length < 20,
          });
          context.commit(types.SET_CHAT_DATA_FETCHED, conversationId);
          context.dispatch('autoTranslateMessages', conversationId);
          return true;
        } catch (error) {
          if (isCurrent())
            context.commit(types.SET_CONVERSATION_HISTORY_ERROR, {
              id: conversationId,
              error: 'latest',
            });
          return false;
        }
      }
    ),
};
